import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { SearchResult } from "@autorepair/contracts";
import { searchApi } from "../lib/api";
import { Spinner } from "../components/ui-modern";
import { Eyebrow, ModernCard, ModernEmptyState, ModernErrorBanner } from "../components/ui-modern";

// "Work Order" kept spelled out (not "job") per the owner's 2026-09-13
// terminology call — see docs/FRONTEND_BACKLOG.md "Round 62".
const KIND_LABELS: Record<string, string> = {
  customer: "Customer",
  vehicle: "Vehicle",
  work_order: "Work Order",
  invoice: "Invoice",
  technician: "Technician",
};

/**
 * The modern theme's Search Results screen — see docs/FRONTEND_BACKLOG.md
 * "Round 62" (pages outside the original 11 mockup screens). No dedicated
 * mockup screen exists for this page; restyled using the established
 * modern vocabulary (ModernCard/ModernStatusPill-style chip/ModernEmptyState)
 * from screens that already exist.
 *
 * Same data-fetching as SearchResultsPageClassic.tsx — identical
 * `["search", q]` query key/queryFn/`enabled` gate, and the exact same
 * `open()` per-kind navigation logic, duplicated here as a thin re-run of
 * Classic's own calls rather than a shared hook, matching every other
 * screen's own tradeoff.
 */
export function SearchResultsPageModern() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") ?? "";

  const { data, isLoading, error } = useQuery({
    queryKey: ["search", q],
    queryFn: () => searchApi.search(q),
    enabled: q.trim().length > 0,
  });

  function open(result: SearchResult): void {
    if (result.kind === "customer" && result.customerNumber != null) navigate(`/customers/${result.customerNumber}`);
    else if (result.kind === "work_order" && result.workOrderNumber != null) navigate(`/work-orders/${result.workOrderNumber}`);
    else if (result.kind === "invoice" && result.invoiceNumber != null) navigate(`/invoices/${result.invoiceNumber}`);
    else if (result.kind === "technician") navigate(`/technicians?technicianId=${result.id}`);
    else if (result.kind === "vehicle" && result.customerNumber != null) navigate(`/customers/${result.customerNumber}?vehicleId=${result.id}`);
  }

  return (
    <div>
      <Eyebrow className="mb-2 text-[11px] text-[#0f6d86]">Search</Eyebrow>
      <h1 className="m-0 mb-4 text-[28px] font-bold text-[#071a33]">Search Results for “{q}”</h1>

      {isLoading && <Spinner />}
      {error && <ModernErrorBanner message="Could not run that search." />}
      {data && data.results.length === 0 && <ModernEmptyState>Nothing found. Try a different name or number.</ModernEmptyState>}

      <div className="flex flex-col gap-2.5">
        {data?.results.map((r) => (
          <button key={`${r.kind}-${r.id}`} type="button" className="text-left" onClick={() => open(r)}>
            <ModernCard className="flex items-center justify-between gap-3 transition-colors hover:border-[#0f6d86]">
              <div className="min-w-0">
                <div className="truncate text-[15px] font-semibold text-[#071a33]">{r.title}</div>
                {r.subtitle && <div className="truncate text-[13px] text-[#6b7a8f]">{r.subtitle}</div>}
              </div>
              {/* Not ModernStatusPill: that component's color/label map is
                  domain status vocabulary (work-order stage, invoice
                  billing state), not this "which kind of record" chip —
                  reusing it here would show the raw `r.kind` string
                  (e.g. "work_order") instead of KIND_LABELS' own text. */}
              <span className="flex-none whitespace-nowrap rounded-[6px] bg-[#eef1f6] px-[9px] py-1 text-[11.5px] font-bold text-[#4a5361]">
                {KIND_LABELS[r.kind]}
              </span>
            </ModernCard>
          </button>
        ))}
      </div>
    </div>
  );
}
